import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { AlertCircle, Pencil, TrendingDown, TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TEAL = "#2EB4AC";
const TEAL_DARK = "#259993";

const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const compactNumber = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return String(Math.round(v));
};

const inrCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 100_000) return `₹${(v / 100_000).toFixed(v % 100_000 === 0 ? 0 : 2)}L`;
  if (Math.abs(v) >= 1_000) return `₹${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

const inrFull = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const Sparkline = ({ points }) => {
  // points: array of numbers (credits)
  if (!points || points.length < 2) return null;
  const w = 56;
  const h = 18;
  const pad = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const path = coords.map((c) => c.join(",")).join(" ");
  const [ex, ey] = coords[coords.length - 1];
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden="true">
      <polyline points={path} fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={ex} cy={ey} r="2.2" fill="#fff" stroke={TEAL} strokeWidth="1.3" />
    </svg>
  );
};

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formCredits, setFormCredits] = useState("");
  const [formRate, setFormRate] = useState("");
  const pillRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cost-tracker`);
      setState(r.data);
    } catch (e) {
      // silent — unauthenticated or offline
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cost-tracker/history`, { params: { weeks: 12 } });
      setHistory(r.data?.history || []);
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    fetchState();
    fetchHistory();
  }, [fetchState, fetchHistory]);

  // Collapse on outside click
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [expanded]);

  const stale = useMemo(() => {
    if (!state) return false;
    if (!state.updated_at) return true;
    try {
      return new Date(state.updated_at) < lastMondayMidnight();
    } catch {
      return true;
    }
  }, [state]);

  const credits = Number(state?.total_credits || 0);
  const rate = Number(state?.cost_per_credit_inr || 0);
  const spent = credits * rate;

  const sparkPoints = useMemo(() => history.map((h) => Number(h.credits || 0)), [history]);

  const openDialog = () => {
    setFormCredits(state?.total_credits != null ? String(state.total_credits) : "");
    setFormRate(state?.cost_per_credit_inr != null ? String(state.cost_per_credit_inr) : "13.70");
    setDialogOpen(true);
  };

  const onSave = async () => {
    const c = parseFloat(formCredits);
    const r = parseFloat(formRate);
    if (Number.isNaN(c) || c < 0) {
      toast.error("Enter a valid credits value");
      return;
    }
    if (Number.isNaN(r) || r < 0) {
      toast.error("Enter a valid per-credit cost");
      return;
    }
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/cost-tracker`, {
        total_credits: c,
        cost_per_credit_inr: r,
      });
      setState(resp.data);
      await fetchHistory();
      toast.success("Cost tracker updated");
      setDialogOpen(false);
      setExpanded(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const previewTotal = useMemo(() => {
    const c = parseFloat(formCredits);
    const r = parseFloat(formRate);
    if (Number.isNaN(c) || Number.isNaN(r)) return 0;
    return c * r;
  }, [formCredits, formRate]);

  // Trend delta (last vs previous week)
  const delta = useMemo(() => {
    if (history.length < 2) return null;
    const last = Number(history[history.length - 1].credits || 0);
    const prev = Number(history[history.length - 2].credits || 0);
    const diff = last - prev;
    const pct = prev > 0 ? (diff / prev) * 100 : 0;
    return { diff, pct, up: diff > 0, down: diff < 0 };
  }, [history]);

  const chartData = useMemo(
    () =>
      history.map((h) => ({
        date: h.week_start,
        credits: Number(h.credits || 0),
        cost: Number(h.cost_inr || 0),
      })),
    [history]
  );

  if (!state) {
    // Render nothing until we know the state (keeps header quiet for anon users)
    return null;
  }

  const lastUpdatedLabel = state.updated_at
    ? (() => {
        try {
          const who = (state.updated_by || "").split("@")[0] || "someone";
          return `Last updated ${format(new Date(state.updated_at), "PPp")} by ${who}`;
        } catch {
          return "";
        }
      })()
    : "Never updated yet — log your first week to start the trendline.";

  return (
    <>
      <div ref={pillRef} className="relative flex items-center" data-testid="cost-tracker-root">
        {!expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
            title="App cost tracker"
            data-testid="cost-tracker-icon-btn"
          >
            <img
              src="/icons/cost-rupee.png"
              alt=""
              className="h-6 w-6 hover:scale-110 transition-transform"
            />
            {stale && (
              <span className="absolute top-1 right-1 flex h-2 w-2" data-testid="cost-tracker-stale-dot">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 ring-1 ring-white" />
              </span>
            )}
          </button>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={openDialog}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openDialog()}
            className="group h-11 rounded-xl border border-slate-200 bg-white flex items-center overflow-hidden cursor-pointer hover:border-slate-300 transition-colors animate-in fade-in slide-in-from-right-2 duration-200"
            data-testid="cost-tracker-pill"
          >
            {/* Left cell: CREDITS */}
            <div className="flex items-center gap-2 px-3 h-full bg-slate-50/50">
              <img src="/icons/cost-rupee.png" alt="" className="h-4 w-4 opacity-80" />
              <div className="flex flex-col leading-none">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  Credits
                </span>
                <span className="text-sm font-bold tabular-nums text-slate-800 mt-0.5" data-testid="cost-tracker-credits">
                  {compactNumber(credits)}
                </span>
              </div>
            </div>
            <div className="w-px h-6 bg-slate-200" />
            {/* Right cell: SPENT + sparkline */}
            <div className="flex items-center gap-2 px-3 h-full">
              <div className="flex flex-col leading-none">
                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: TEAL }}>
                  Spent
                </span>
                <span className="text-sm font-bold tabular-nums text-slate-800 mt-0.5" data-testid="cost-tracker-spent">
                  {inrCompact(spent)}
                </span>
              </div>
              <Sparkline points={sparkPoints} />
              {stale ? (
                <span className="relative flex h-2 w-2" title="Update needed — new week">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 ring-1 ring-white" />
                </span>
              ) : (
                <Pencil className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          {stale && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
              data-testid="cost-tracker-stale-alert"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>It&apos;s a new week — please update the credits consumed since last Monday.</span>
            </div>
          )}

          {/* Trendline */}
          {chartData.length >= 2 ? (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    Week-on-week credits
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Last {chartData.length} week{chartData.length === 1 ? "" : "s"} · Mondays
                  </div>
                </div>
                {delta && delta.diff !== 0 && (
                  <div
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      delta.up
                        ? "bg-red-50 text-red-600"
                        : "bg-emerald-50 text-emerald-600"
                    }`}
                    data-testid="cost-tracker-trend-chip"
                  >
                    {delta.up ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {delta.up ? "+" : ""}
                    {Math.round(delta.diff)} cr ({delta.pct >= 0 ? "+" : ""}
                    {Math.round(delta.pct)}%)
                  </div>
                )}
              </div>
              <div style={{ width: "100%", height: 176 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="costTrackerFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => {
                        try {
                          return format(new Date(v), "d MMM");
                        } catch {
                          return v;
                        }
                      }}
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => compactNumber(v)}
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        fontSize: 12,
                      }}
                      formatter={(v, name) => [
                        name === "credits" ? compactNumber(v) : inrFull(v),
                        name === "credits" ? "Credits" : "Cost",
                      ]}
                      labelFormatter={(v) => {
                        try {
                          return format(new Date(v), "PPP");
                        } catch {
                          return v;
                        }
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="credits"
                      stroke={TEAL}
                      strokeWidth={2}
                      fill="url(#costTrackerFill)"
                      dot={{ r: 3, fill: "#fff", stroke: TEAL, strokeWidth: 2 }}
                      activeDot={{ r: 4, fill: "#fff", stroke: TEAL, strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-[12px] text-slate-500">
              Save updates each week to build the trendline.
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                Total credits consumed
              </label>
              <Input
                type="number"
                inputMode="decimal"
                step="1"
                min="0"
                value={formCredits}
                onChange={(e) => setFormCredits(e.target.value)}
                placeholder="0"
                data-testid="cost-tracker-credits-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                Cost per credit (₹)
              </label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={formRate}
                onChange={(e) => setFormRate(e.target.value)}
                placeholder="13.70"
                data-testid="cost-tracker-rate-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Default ₹13.70 — edit anytime as billing changes.
              </p>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-xl bg-slate-50/60 border border-slate-100 px-4 py-3 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Estimated spend
            </span>
            <span className="text-xl font-bold tabular-nums text-slate-800" data-testid="cost-tracker-preview-total">
              {inrFull(previewTotal)}
            </span>
          </div>

          <p className="text-[11px] text-slate-400 -mt-1" data-testid="cost-tracker-last-updated">
            {lastUpdatedLabel}
          </p>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="cost-tracker-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              style={{ backgroundColor: TEAL }}
              className="text-white hover:!bg-[--teal-dark]"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
              data-testid="cost-tracker-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
